export const usePwaUpdate = () => {
  onMounted(() => {
    const { $appDeviceInfo } = useNuxtApp()
    // Only run PWA version checks for actual PWA installs.
    // TWA also runs in standalone mode but updates via the Play Store, not this mechanism.
    const isPWA = $appDeviceInfo?.displayMode === 'pwa' ||
      (window.navigator as any).standalone === true // iOS PWA (Safari)

    if (!isPWA) return

    /*
       * Checks for updates to the Progressive Web App (PWA) version.
       *
       * This function fetches the latest app version from a JSON file and compares it
       * with the version stored in localStorage. If a new version is available, it prompts
       * the user to reload the app. If no version is stored, it saves the latest version
       * to localStorage.
       *
       * @async
       * @function checkVersion
       * @returns {Promise<void>} - Resolves when the version check is complete.
       */
    const checkVersion = async () => {
      try {
        // Fetch the latest app version from the server
        const res = await fetch('/pwa-app-version.json', { cache: 'no-store' })
        const { version: latestVersion } = await res.json()

        // Retrieve the stored app version from localStorage
        const storedVersion = localStorage.getItem('app-version')

        // If a new version is available, prompt the user to reload the app
        if (storedVersion && storedVersion !== latestVersion && confirm('A new version of the app is available. Do you want to reload?')) {
          localStorage.setItem('app-version', latestVersion)
          await clearAllCaches()
          window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now()
        } else if (!storedVersion) {
          // If no version is stored, save the latest version to localStorage
          localStorage.setItem('app-version', latestVersion)
        }
      } catch (error) {
        // Log a warning if the version check fails
        console.warn('[PWA] Version check failed:', error)
      }
    }

    const clearAllCaches = async () => {
      const cacheNames = await caches.keys()

      await Promise.all(
        cacheNames
          .filter(name => !name.startsWith('workbox-')) // Exclude Workbox-managed caches
          .map(name => caches.delete(name))
      )
    }

    // Initial check on load
    checkVersion()

    // Periodic checks in every 15 minutes
    setInterval(checkVersion, 15 * 60 * 1000)

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now()
      })
    }
  })
}